import { useCallback, useReducer, useRef } from "react";
import {
  uploadPdfsToStorage,
  callParseFunction,
} from "../../../utils/parsePurchaseAgreement";
import { PARSER_CAPS } from "../../../utils/parserCaps";
import { auth } from "../../../firebase/firebaseConfig";
import { APPLY_KEY_MAP, INFO_FIELDS } from "./fieldMetadata";

const MAX_IN_PROGRESS_RETRIES = 3;
const IN_PROGRESS_BACKOFF_MS = 3000;

// User-facing fallback messages per failure phase. Backend-supplied
// errorMessages take precedence on the parse-response phase so specific
// codes like NO_PA still surface their own copy.
const PHASE_MESSAGES = {
  upload:
    "We couldn't upload your files. Check your connection and try again.",
  "parse-call":
    "We couldn't reach the parser. Please try again in a moment.",
  "parse-response":
    "We couldn't extract anything from this document. Please review the file.",
};

// State machine, per spec §5.1:
//   idle      → single button
//   picking   → drop zone + file list + Parse Documents
//   uploading → progress bars
//   parsing   → spinner
//   confirm   → field checklist + Apply / Re-apply
//   error     → message + Try again / Cancel
//
// `importData` is the persistent slice that survives parent re-renders
// via the `savedImport` prop; everything else is transient.
function init(savedImport) {
  return {
    stage: savedImport ? "confirm" : "idle",
    files: [],
    fileIds: [],
    progress: {},
    errorInfo: null,
    importData: savedImport,
    applyState: "idle", // 'idle' | 'applying'
  };
}

function reducer(state, action) {
  switch (action.type) {
    case "GO_PICKING":
      return { ...state, stage: "picking", errorInfo: null, progress: {} };
    case "APPEND_FILES":
      return {
        ...state,
        files: [...state.files, ...action.files],
        fileIds: [...state.fileIds, ...action.fileIds],
      };
    case "REMOVE_FILE_AT":
      return {
        ...state,
        files: state.files.filter((_, i) => i !== action.idx),
        fileIds: state.fileIds.filter((_, i) => i !== action.idx),
      };
    case "BEGIN_UPLOAD":
      return { ...state, stage: "uploading", progress: {} };
    case "BEGIN_PARSE":
      return { ...state, stage: "parsing" };
    case "PROGRESS":
      return {
        ...state,
        progress: { ...state.progress, [action.fileId]: action.fraction },
      };
    case "PARSE_OK":
      return { ...state, stage: "confirm", importData: action.importData };
    case "ERROR":
      return { ...state, stage: "error", errorInfo: action.errorInfo };
    case "RESET":
      return init(null);
    case "SET_IMPORT_DATA":
      return { ...state, importData: action.importData };
    case "APPLY_BEGIN":
      return { ...state, applyState: "applying" };
    case "APPLY_END":
      return { ...state, applyState: "idle" };
    default:
      return state;
  }
}

export function useImportPurchaseAgreement({
  onApply,
  savedImport = null,
  onSavedImportChange,
  onFilesPicked,
} = {}) {
  // savedImport is the SOLE init source — no useEffect resync. Spec line
  // 99 explicitly warns: do not add effects that reset a reviewed parse.
  const [state, dispatch] = useReducer(reducer, savedImport, init);
  const fileInputRef = useRef(null);

  // Mirrors the original `persistImport`: dispatch the new importData
  // and notify the parent in the same synchronous pass.
  const setImportData = useCallback(
    (next) => {
      dispatch({ type: "SET_IMPORT_DATA", importData: next });
      if (onSavedImportChange) onSavedImportChange(next);
    },
    [onSavedImportChange]
  );

  const reset = useCallback(() => {
    dispatch({ type: "RESET" });
    if (onSavedImportChange) onSavedImportChange(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [onSavedImportChange]);

  const onProgress = useCallback((fileId, fraction) => {
    dispatch({ type: "PROGRESS", fileId, fraction });
  }, []);

  const goPicking = useCallback(() => {
    dispatch({ type: "GO_PICKING" });
  }, []);

  const handleFileSelect = (incoming) => {
    if (!incoming) return;
    const list = Array.from(incoming).filter(
      (f) => f && (f.type === "application/pdf" || /\.pdf$/i.test(f.name))
    );
    // Cumulative count check — backend assertWithinCaps sees the full
    // request, so per-batch checking would let two small batches bypass
    // the cap.
    const overCount = state.files.length + list.length > PARSER_CAPS.maxFiles;
    const overSize = list.some((f) => f.size > PARSER_CAPS.maxBytes);
    if (overCount || overSize) {
      const mb = Math.round(PARSER_CAPS.maxBytes / 1024 / 1024);
      dispatch({
        type: "ERROR",
        errorInfo: {
          errorCode: "TOO_LARGE",
          errorMessage: `Limit: ${PARSER_CAPS.maxFiles} files, ${mb} MB each.`,
        },
      });
      return;
    }
    // One fileId per file at selection so React keys + progress map
    // survive duplicate filenames and re-renders.
    const newIds = list.map(() => Math.random().toString(36).slice(2, 14));
    dispatch({ type: "APPEND_FILES", files: list, fileIds: newIds });
    // Fire with the first PDF so callers (e.g. Add New Deal) can
    // auto-attach it to a separate "Attach Purchase Agreement" control.
    if (onFilesPicked && list.length > 0) {
      onFilesPicked(list[0]);
    }
  };

  const handleRemoveFile = (idx) => {
    dispatch({ type: "REMOVE_FILE_AT", idx });
  };

  const runParseWithRetry = async (requestId) => {
    let attempt = 0;
    // Initial call + up to MAX_IN_PROGRESS_RETRIES retries.
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const response = await callParseFunction(requestId);
      if (
        response &&
        response.status === "error" &&
        response.errorCode === "PARSE_IN_PROGRESS" &&
        attempt < MAX_IN_PROGRESS_RETRIES
      ) {
        attempt += 1;
        await new Promise((resolve) =>
          setTimeout(resolve, IN_PROGRESS_BACKOFF_MS)
        );
        continue;
      }
      return response;
    }
  };

  const handleParse = async () => {
    if (!state.files.length) return;
    const userId = auth?.currentUser?.uid;
    if (!userId) {
      dispatch({
        type: "ERROR",
        errorInfo: {
          errorCode: "NOT_AUTHENTICATED",
          errorMessage:
            "You must be signed in to import a purchase agreement.",
        },
      });
      return;
    }
    dispatch({ type: "BEGIN_UPLOAD" });

    // Phase: upload — Storage is reachable + signed URL accepted.
    let requestId;
    try {
      ({ requestId } = await uploadPdfsToStorage(
        state.files,
        userId,
        onProgress,
        state.fileIds
      ));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("PDF import upload failed:", err);
      dispatch({
        type: "ERROR",
        errorInfo: {
          errorCode: "UPLOAD_FAILED",
          errorMessage: PHASE_MESSAGES.upload,
          phase: "upload",
        },
      });
      return;
    }

    dispatch({ type: "BEGIN_PARSE" });

    // Phase: parse-call — Cloud function reachable (vs. throw on transport).
    let response;
    try {
      response = await runParseWithRetry(requestId);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("PDF parse call failed:", err);
      dispatch({
        type: "ERROR",
        errorInfo: {
          errorCode: "PARSE_CALL_FAILED",
          errorMessage: PHASE_MESSAGES["parse-call"],
          phase: "parse-call",
        },
      });
      return;
    }

    // Phase: parse-response — Function returned; may still be a logical
    // error (NO_PA, PARSE_IN_PROGRESS, etc.). Backend errorMessage wins
    // when present.
    if (!response || response.status === "error") {
      dispatch({
        type: "ERROR",
        errorInfo: {
          errorCode: response?.errorCode || "UNKNOWN",
          errorMessage:
            response?.errorMessage || PHASE_MESSAGES["parse-response"],
          phase: "parse-response",
        },
      });
      return;
    }

    // Default-check every parsed (non-null) field.
    const initialChecked = {};
    const parsed = response.parsedFields || {};
    Object.keys(parsed).forEach((k) => {
      if (INFO_FIELDS.has(k)) return; // informational rows have no checkbox
      if (parsed[k] !== null && parsed[k] !== undefined && parsed[k] !== "") {
        initialChecked[k] = true;
      }
    });
    const next = {
      result: response,
      applied: false,
      checked: initialChecked,
      collapsed: false,
    };
    dispatch({ type: "PARSE_OK", importData: next });
    if (onSavedImportChange) onSavedImportChange(next);
  };

  const handleApply = async () => {
    if (!state.importData?.result) return;
    const parsed = state.importData.result.parsedFields || {};
    const checked = state.importData.checked || {};
    const payload = {};
    Object.keys(parsed).forEach((k) => {
      if (INFO_FIELDS.has(k)) return; // never apply informational fields
      if (
        checked[k] &&
        parsed[k] !== null &&
        parsed[k] !== undefined &&
        parsed[k] !== ""
      ) {
        const targetKey = APPLY_KEY_MAP[k] || k;
        payload[targetKey] = parsed[k];
      }
    });
    dispatch({ type: "APPLY_BEGIN" });
    try {
      await onApply(payload);
      const next = { ...state.importData, applied: true };
      dispatch({ type: "SET_IMPORT_DATA", importData: next });
      if (onSavedImportChange) onSavedImportChange(next);
    } catch (err) {
      dispatch({
        type: "ERROR",
        errorInfo: {
          errorCode: "APPLY_FAILED",
          errorMessage:
            err?.message ||
            "We couldn't apply the parsed fields. Please try again.",
          phase: "apply",
        },
      });
    } finally {
      dispatch({ type: "APPLY_END" });
    }
  };

  const toggleChecked = (key) => {
    if (!state.importData) return;
    const prev = state.importData.checked || {};
    setImportData({
      ...state.importData,
      checked: { ...prev, [key]: !prev[key] },
    });
  };

  const toggleCollapsed = () => {
    if (!state.importData) return;
    setImportData({
      ...state.importData,
      collapsed: !state.importData.collapsed,
    });
  };

  return {
    state,
    fileInputRef,
    handlers: {
      goPicking,
      handleFileSelect,
      handleRemoveFile,
      reset,
      handleParse,
      handleApply,
      toggleChecked,
      toggleCollapsed,
    },
  };
}
